import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard({ user, logout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [period, setPeriod] = useState("24h");
  
  // Logs pagination and filters
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsFilter, setLogsFilter] = useState({
    status: "",
    action: "",
    search: ""
  });
  
  // Users pagination
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  
  // Add admin form
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);

  // Delete user state
  const [userToDelete, setUserToDelete] = useState(null);

  // Bulk delete state
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      const response = await axios.delete(`${API}/admin/users/${userToDelete.user_id}`);
      const deleted = response.data.deleted;
      toast.success(`Purged: ${deleted.users} users, ${deleted.submissions} submissions, ${deleted.logs} logs`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setUserToDelete(null);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all visible users
      const newSelected = new Set(users.map(u => u.user_id));
      setSelectedUserIds(newSelected);
    } else {
      setSelectedUserIds(new Set());
    }
  };

  const handleSelectUser = (userId, checked) => {
    const newSelected = new Set(selectedUserIds);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleBulkDelete = async () => {
    setDeletingMultiple(true);
    try {
      const response = await axios.post(`${API}/admin/users/bulk-delete`, {
        user_ids: Array.from(selectedUserIds)
      });
      const deleted = response.data.deleted;
      toast.success(`Bulk purge complete: ${deleted.users} users deleted`);
      setSelectedUserIds(new Set());
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk delete users");
    } finally {
      setDeletingMultiple(false);
      setShowBulkDeleteDialog(false);
    }
  };
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats?period=${period}`);
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        toast.error("Failed to fetch stats");
      }
    }
  }, [period, navigate]);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: logsPage,
        limit: 20
      });
      if (logsFilter.status) params.append("status", logsFilter.status);
      if (logsFilter.action) params.append("action", logsFilter.action);
      if (logsFilter.search) params.append("search", logsFilter.search);
      
      const response = await axios.get(`${API}/admin/logs?${params.toString()}`);
      setLogs(response.data.logs);
      setLogsTotalPages(response.data.pages);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  }, [logsPage, logsFilter]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users?page=${usersPage}&limit=20`);
      setUsers(response.data.users);
      setUsersTotalPages(response.data.pages);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  }, [usersPage]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchStats();
      setLoading(false);
    };
    loadData();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLogs();
    } else if (activeTab === "users") {
      fetchUsers();
    }
  }, [activeTab, fetchLogs, fetchUsers]);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) return;
    
    setAddingAdmin(true);
    try {
      const formData = new FormData();
      formData.append("email", newAdminEmail.trim());
      
      await axios.post(`${API}/admin/add-admin`, formData);
      toast.success("Admin added successfully");
      setNewAdminEmail("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add admin");
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleCleanupLogs = async () => {
    if (!window.confirm("Delete all logs older than 30 days?")) return;
    
    try {
      const response = await axios.delete(`${API}/admin/logs/cleanup`);
      toast.success(`Deleted ${response.data.deleted_count} old logs`);
      fetchLogs();
      fetchStats();
    } catch (error) {
      toast.error("Failed to cleanup logs");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    if (status === "success") {
      return <Badge className="bg-green-500">Success</Badge>;
    }
    return <Badge variant="destructive">Error</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center graph-paper-bg">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen graph-paper-bg">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="text-2xl">🛡️</span>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">System Telemetry & Monitoring</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to App
            </Button>
            <Button variant="ghost" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 border-b">
          {["overview", "logs", "users"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize transition-colors ${
                activeTab === tab
                  ? "border-b-2 border-primary text-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex gap-2">
              {["24h", "7d", "30d"].map((p) => (
                <Button
                  key={p}
                  variant={period === p ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPeriod(p)}
                >
                  {p === "24h" ? "24 Hours" : p === "7d" ? "7 Days" : "30 Days"}
                </Button>
              ))}
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Requests</CardDescription>
                  <CardTitle className="text-3xl">{stats.summary?.total_requests || 0}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Active Users</CardDescription>
                  <CardTitle className="text-3xl">{stats.summary?.unique_active_users || 0}</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Error Rate</CardDescription>
                  <CardTitle className="text-3xl text-red-500">{stats.summary?.error_rate || 0}%</CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Avg Response Time</CardDescription>
                  <CardTitle className="text-3xl">{stats.summary?.avg_response_time_ms || 0}ms</CardTitle>
                </CardHeader>
              </Card>
            </div>

            {/* User Counts */}
            <Card>
              <CardHeader>
                <CardTitle>User Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold">{stats.user_counts?.total || 0}</div>
                    <div className="text-sm text-gray-500">Total Users</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{stats.user_counts?.students || 0}</div>
                    <div className="text-sm text-gray-500">Students</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{stats.user_counts?.parents || 0}</div>
                    <div className="text-sm text-gray-500">Parents</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{stats.user_counts?.teachers || 0}</div>
                    <div className="text-sm text-gray-500">Teachers</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Login Stats */}
            {stats.login_stats?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Login Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stats.login_stats.map((login, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                        <span className="font-medium">
                          {login.type === "user_login_oauth" ? "OAuth Login" : "Student Login"}
                        </span>
                        <div className="flex gap-4">
                          <span className="text-green-600">✓ {login.successful}</span>
                          <span className="text-red-600">✗ {login.failed}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Breakdown */}
            {stats.action_breakdown?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Top Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stats.action_breakdown.slice(0, 10).map((action, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 border-b last:border-0">
                        <span className="font-mono text-sm">{action.action}</span>
                        <div className="flex gap-4">
                          <span>{action.count} calls</span>
                          {action.errors > 0 && (
                            <span className="text-red-500">{action.errors} errors</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Errors */}
            {stats.recent_errors?.length > 0 && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-600">Recent Errors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.recent_errors.map((error, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-mono text-sm">{error.action}</span>
                          <span className="text-xs text-gray-500">{formatDate(error.timestamp)}</span>
                        </div>
                        <p className="text-sm text-red-700">{error.error_message || "Unknown error"}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {error.endpoint} | User: {error.user_id || "Anonymous"}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === "logs" && (
          <div className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-4">
                  <Input
                    placeholder="Search logs..."
                    value={logsFilter.search}
                    onChange={(e) => setLogsFilter({ ...logsFilter, search: e.target.value })}
                    className="w-64"
                  />
                  <select
                    value={logsFilter.status}
                    onChange={(e) => setLogsFilter({ ...logsFilter, status: e.target.value })}
                    className="px-3 py-2 border rounded-md"
                  >
                    <option value="">All Status</option>
                    <option value="success">Success</option>
                    <option value="error">Error</option>
                  </select>
                  <Input
                    placeholder="Filter by action..."
                    value={logsFilter.action}
                    onChange={(e) => setLogsFilter({ ...logsFilter, action: e.target.value })}
                    className="w-48"
                  />
                  <Button onClick={fetchLogs}>Apply Filters</Button>
                  <Button variant="outline" onClick={handleCleanupLogs}>
                    Cleanup Old Logs
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Logs Table */}
            <Card>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Timestamp</th>
                        <th className="text-left p-2">Action</th>
                        <th className="text-left p-2">User</th>
                        <th className="text-left p-2">Status</th>
                        <th className="text-left p-2">Duration</th>
                        <th className="text-left p-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2 text-xs">{formatDate(log.timestamp)}</td>
                          <td className="p-2 font-mono text-xs">{log.action}</td>
                          <td className="p-2 text-xs">
                            {log.user_id ? (
                              <span>{log.user_role || "unknown"}</span>
                            ) : (
                              <span className="text-gray-400">anonymous</span>
                            )}
                          </td>
                          <td className="p-2">{getStatusBadge(log.status)}</td>
                          <td className="p-2">{log.duration_ms}ms</td>
                          <td className="p-2 text-xs text-red-600 max-w-xs truncate">
                            {log.error_message || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-500">
                    Page {logsPage} of {logsTotalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={logsPage <= 1}
                      onClick={() => setLogsPage(logsPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={logsPage >= logsTotalPages}
                      onClick={() => setLogsPage(logsPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-4">
            {/* Add Admin Form */}
            <Card>
              <CardHeader>
                <CardTitle>Add Admin</CardTitle>
                <CardDescription>
                  Grant admin access to a user. They must have logged in at least once.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddAdmin} className="flex gap-4">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={newAdminEmail}
                    onChange={(e) => setNewAdminEmail(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={addingAdmin}>
                    {addingAdmin ? "Adding..." : "Add Admin"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>All Users</CardTitle>
                {selectedUserIds.size > 0 && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => setShowBulkDeleteDialog(true)}
                  >
                    Delete {selectedUserIds.size} Selected
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="p-2 w-10">
                          <input 
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                            checked={users.length > 0 && selectedUserIds.size === users.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                          />
                        </th>
                        <th className="text-left p-2">Display Name</th>
                        <th className="text-left p-2">Role</th>
                        <th className="text-left p-2">Admin</th>
                        <th className="text-left p-2">Created</th>
                        <th className="text-left p-2">Last Activity</th>
                        <th className="text-left p-2">Activity (7d)</th>
                        <th className="text-left p-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users?.length > 0 ? (
                        users.map((u, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-2">
                            <input 
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                              checked={selectedUserIds.has(u.user_id)}
                              onChange={(e) => handleSelectUser(u.user_id, e.target.checked)}
                            />
                          </td>
                          <td className="p-2">{u.display_name}</td>
                          <td className="p-2">
                            <Badge variant="outline">{u.role || "unset"}</Badge>
                          </td>
                          <td className="p-2">
                            {u.is_admin && <Badge className="bg-purple-500">Admin</Badge>}
                          </td>
                          <td className="p-2 text-xs">{formatDate(u.created_at)}</td>
                          <td className="p-2 text-xs">{formatDate(u.last_activity)}</td>
                          <td className="p-2">{u.activity_count_7d}</td>
                          <td className="p-2">
                            {!u.is_admin && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-red-500 hover:text-red-700 hover:bg-red-50" 
                                onClick={() => setUserToDelete(u)}
                                title="Purge User"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center p-4 text-gray-500">No users found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-500">
                    Page {usersPage} of {usersTotalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={usersPage <= 1}
                      onClick={() => setUsersPage(usersPage - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={usersPage >= usersTotalPages}
                      onClick={() => setUsersPage(usersPage + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      {/* Single Delete Dialog */}
      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Purge User Data?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. It will permanently delete 
              <span className="font-bold text-red-600"> {userToDelete?.display_name} </span> 
              and ALL associated data, including:
              <ul className="list-disc list-inside mt-2 text-gray-700">
                  <li>User account and profile</li>
                  {userToDelete?.role === "parent" && <li>All linked student accounts</li>}
                  <li>Drill history and configurations</li>
                  <li>Test submissions and results</li>
                  <li>Activity logs and sessions</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700 text-white" 
              onClick={confirmDeleteUser}
            >
              Yes, Purge User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Multiple Users?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. It will permanently delete 
              <span className="font-bold text-red-600"> {selectedUserIds.size} users </span> 
              and ALL associated data (profiles, students, logs, submissions).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700 text-white" 
              onClick={handleBulkDelete}
              disabled={deletingMultiple}
            >
              {deletingMultiple ? "Deleting..." : "Yes, Delete All"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AdminDashboard;
